import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  ListSubheader,
  Tabs,
  Tab,
  Grid,
  IconButton,
  Typography,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useGenerateTasksForOrder } from '../utils/orderTaskGenerator';
import { adjustPortionsForEdit } from '../utils/harvestPortionAdjuster';
import PriceField from './PriceField';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  getDoc,
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  deleteDoc,
  onSnapshot,
} from 'firebase/firestore';
import { format, parseISO } from 'date-fns';
import { db } from '../firebaseConfig';
import { getNextOrderNumber } from './orderCounter';
import { AppContext } from '../AppContext';
import ConfirmationDialog from '../components/ConfirmationDialog';

import { trackFeature } from '../utils/analytics';

// Helper to create a new empty produce entry (with one blend line)
const createProduceEntry = (defaultCurrency) => ({
  items: [{ harvestId: '', proportion: '1' }],
  portions: '1',
  price: '',
  priceCurrency: defaultCurrency,
});

export default function AddOrder({
  open,
  onClose,
  onAdd,
  editMode = false,
  orderToEdit = {},
  setSuccess,
  setError,
}) {
  const { user, customers, harvests, seeds, userData, farmData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;

  // -- Note-template state/hooks --
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [noteTplConfirmOpen, setNoteTplConfirmOpen] = useState(false);
  const [noteTplName, setNoteTplName] = useState('');

  // load existing note-templates whenever dialog opens
  useEffect(() => {
    if (!open || !user?.uid || !activeFarmId) {
      setNoteTemplates([]);
      return;
    }
    const colRef = collection(db, 'farms', activeFarmId, 'orderNoteTemplates');
    const unsubscribe = onSnapshot(colRef, snapshot => {
      setNoteTemplates(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsubscribe;
  }, [open, user, activeFarmId]);

  // Customer dropdown options
  const customerOptions = useMemo(
    () => customers.map((c) => c.businessName),
    [customers]
  );

  // Only show non-archived harvests
  const officialHarvests = useMemo(
    () => harvests.filter((h) => !h.archived),
    [harvests]
  );

  // Settings
  const defaultDateFormat = farmData?.settings?.dateFormat || 'dd-MM-yyyy';
  const defaultCurrency = farmData?.settings?.currency || '£';
  const shortFormat = defaultDateFormat.includes('yyyy')
    ? defaultDateFormat.replace(/[-/]?yyyy/, '')
    : defaultDateFormat;

  const generateTaskForOrderDelivery = useGenerateTasksForOrder();

  // Map seedId → seed object for labels
  const seedLookup = useMemo(() => {
    return seeds.reduce((acc, s) => {
      acc[s.id] = s;
      return acc;
    }, {});
  }, [seeds]);

  // Main form state
  const [newOrder, setNewOrder] = useState({
    customerName: '',
    deliveryDate: null,
    recurring: false,
    notes: '',
    produce: [createProduceEntry(defaultCurrency)],
    deliveryPrice: '',
    deliveryPriceCurrency: defaultCurrency,
  });
  const [produceTabIndex, setProduceTabIndex] = useState(0);
  const [originalProduce, setOriginalProduce] = useState([]);

  const [touched, setTouched] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  const renderCurrencySelect = (value, onChange) => (
  <Select
    variant="standard"
    disableUnderline
    sx={currencySelectSx}
    value={value}
    onChange={onChange}
  >
    <MenuItem value="£">£</MenuItem>
    <MenuItem value="$">$</MenuItem>
    <MenuItem value="€">€</MenuItem>
  </Select>
);


  // When dialog opens, either reset or populate from orderToEdit
  // whenever the dialog opens (or defaultCurrency changes), reset the form
  useEffect(() => {
    if (!open) return;

    setTouched({});
    setFormSubmitted(false);
    setProduceTabIndex(0);

    // 1) Edit mode: load exactly as it was saved
    if (editMode && orderToEdit?.id) {
      let parsed = [];
      if (Array.isArray(orderToEdit.produce)) {
        parsed = orderToEdit.produce.map((entry) => ({
          items: entry.items.map((it) => ({
            harvestId: it.harvestId,
            proportion: Number(it.proportion) || 0,
          })),
          portions: Number(entry.portions) || 0,
          price: entry.price || '',
          priceCurrency: entry.priceCurrency || defaultCurrency,
        }));
      } else if (typeof orderToEdit.produce === 'string') {
        parsed = orderToEdit.produce.split('/').map((hid) => ({
          items: [{ harvestId: hid, proportion: 1 }],
          portions: 1,
          price: '',
          priceCurrency: defaultCurrency,
        }));
      }

      setOriginalProduce(parsed);
      setNewOrder({
        customerName: orderToEdit.customerName || '',
        deliveryDate: orderToEdit.deliveryDate
          ? new Date(orderToEdit.deliveryDate)
          : null,
        recurring: orderToEdit.recurring || false,
        notes: orderToEdit.notes || '',
        produce: parsed,
        deliveryPrice: orderToEdit.deliveryPrice || '',
        deliveryPriceCurrency: orderToEdit.deliveryPriceCurrency || defaultCurrency,
      });
      return;
    }

    // 2) Clone prefilling: prefill form but treat as a new order
    if (!editMode && orderToEdit) {
      const raw = orderToEdit;
      const clonedProduce = Array.isArray(raw.produce)
        ? raw.produce.map((entry) => ({
          items: entry.items.map((it) => ({
            harvestId: it.harvestId,
            proportion: it.proportion,
          })),
          portions: entry.portions,
          price: entry.price || '',
          priceCurrency: entry.priceCurrency || defaultCurrency,
        }))
        : raw.produce.split('/').map((hid) => ({
          items: [{ harvestId: hid, proportion: 1 }],
          portions: 1,
          price: '',
          priceCurrency: defaultCurrency,
        }));

      setOriginalProduce([]);
      setNewOrder({
        customerName: raw.customerName,
        deliveryDate: raw.deliveryDate ? new Date(raw.deliveryDate) : null,
        recurring: raw.recurring || false,
        notes: raw.notes || '',
        produce: clonedProduce,
        deliveryPrice: raw.deliveryPrice || '',
        deliveryPriceCurrency: raw.deliveryPriceCurrency || defaultCurrency,
      });
      return;
    }

    // 3) Brand‑new: empty form
    setOriginalProduce([]);
    setNewOrder({
      customerName: '',
      deliveryDate: null,
      recurring: false,
      notes: '',
      produce: [createProduceEntry(defaultCurrency)],
      deliveryPrice: '',
      deliveryPriceCurrency: defaultCurrency,
    });
  }, [open, editMode, orderToEdit, defaultCurrency]);




  // Mark a field touched on blur
  const handleBlur = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  // Build combined harvest options (official + extras)
  const mergedHarvestsOptions = useMemo(() => {
    const official = officialHarvests.map((h) => {
      const dateLabel = h.harvestDate
        ? format(parseISO(h.harvestDate), shortFormat)
        : 'No Date';
      let seedDisplay = 'Unknown';
      if (h.seeds?.length) {
        seedDisplay = h.seeds
          .map((s) =>
            s.seedId && seedLookup[s.seedId]
              ? seedLookup[s.seedId].name
              : 'Unknown'
          )
          .join('/');
      } else if (h.seedId) {
        seedDisplay = seedLookup[h.seedId]?.name || 'Unknown';
      }
      const portionsLeftRaw = h.portionsLeft ?? h.seeds?.[0]?.portionsLeft;
      const portionsLeftNum = Number(portionsLeftRaw);
      const portionsLeft = Number.isFinite(portionsLeftNum)
        ? portionsLeftNum.toFixed(2)
        : '';
      const portionsSuffix = portionsLeft ? ` (${portionsLeft} left)` : '';
      return { id: String(h.id), label: `${dateLabel} - ${seedDisplay}${portionsSuffix}` };
    });

    // Any harvestIds in existing “items” that aren’t in official → extras
    const extra = [];
    (Array.isArray(newOrder.produce) ? newOrder.produce : []).forEach((p) =>
      p.items.forEach((it) => {
        if (
          it.harvestId &&
          !official.find((o) => o.id === String(it.harvestId))
        ) {
          extra.push({ id: it.harvestId, label: 'Unknown' });
        }
      })
    );

    return { official, extra };
  }, [officialHarvests, newOrder.produce, seedLookup, shortFormat]);

  // --- Tab controls ---
  const handleProduceTabChange = (_, newI) => setProduceTabIndex(newI);
  const handleAddProduceTab = () => {
    setNewOrder((prev) => ({
      ...prev,
      produce: [...prev.produce, createProduceEntry(defaultCurrency)],
    }));
    setProduceTabIndex((i) => i + 1);
  };
  const handleDeleteProduceTab = (index) => {
    if (newOrder.produce.length <= 1) return;
    setNewOrder((prev) => ({
      ...prev,
      produce: prev.produce.filter((_, i) => i !== index),
    }));
    setProduceTabIndex((i) => (i > index ? i - 1 : 0));
  };

  // Update “portions”/“price” fields on the active tab
  const handleProduceFieldChange = (field, value) => {
    setNewOrder((prev) => {
      const produce = [...prev.produce];
      produce[produceTabIndex] = {
        ...produce[produceTabIndex],
        [field]: value,
      };
      return { ...prev, produce };
    });
  };

  // --- Blend‐item handlers ---

  // Add a new line to the blend
  const addBlendItem = () => {
    setNewOrder((prev) => {
      const produce = [...prev.produce];
      produce[produceTabIndex].items.push({ harvestId: '', proportion: '' });
      return { ...prev, produce };
    });
  };

  // Remove a line (only allowed for idx > 0)
  const removeBlendItem = (idx) => {
    if (idx === 0) return;
    setNewOrder((prev) => {
      const produce = [...prev.produce];
      produce[produceTabIndex].items.splice(idx, 1);
      return { ...prev, produce };
    });
  };

  // When user changes harvestId or proportion text
  const handleBlendItemChange = (idx, field, rawValue) => {
    setNewOrder((prev) => {
      const produce = [...prev.produce];
      const items = [...produce[produceTabIndex].items];
      items[idx][field] = rawValue;

      // If they changed the harvestId, rebalance **all** proportions equally
      if (field === 'harvestId') {
        const n = items.length;
        const defaultProp = n === 1 ? '1' : (1 / n).toFixed(2);
        items.forEach((it) => {
          it.proportion = defaultProp;
        });
      }

      produce[produceTabIndex].items = items;
      return { ...prev, produce };
    });
  };

  // When a proportion field blurs, clamp it and rebalance others
  const rebalanceOthers = (editedIdx) => {
    setNewOrder((prev) => {
      const produce = [...prev.produce];
      const items = produce[produceTabIndex].items.map((it) => ({
        ...it,
      }));

      let v = parseFloat(items[editedIdx].proportion);
      if (isNaN(v)) v = 0;
      v = Math.max(0, Math.min(1, v));
      items[editedIdx].proportion = v.toFixed(2);

      const rem = 1 - v;
      const others = items.length - 1;
      const share = others > 0 ? (rem / others).toFixed(2) : '0';
      items.forEach((_, j) => {
        if (j !== editedIdx) items[j].proportion = share;
      });

      produce[produceTabIndex].items = items;
      return { ...prev, produce };
    });
  };

  // Quick check: any line missing a harvest?
  const hasMissingBlendHarvest = () => {
    return newOrder.produce[produceTabIndex].items.some(
      (it) => !it.harvestId
    );
  };

  const activeProduce = newOrder.produce[produceTabIndex];
  const isBlendMode = activeProduce.items.length > 1;

  const handleBlendModeToggle = (_, checked) => {
    setNewOrder((prev) => {
      const produce = [...prev.produce];
      const entry = { ...produce[produceTabIndex] };
      const items = [...entry.items];

      if (checked) {
        if (items.length === 1) {
          const first = { ...items[0], proportion: '0.50' };
          entry.items = [first, { harvestId: '', proportion: '0.50' }];
        }
      } else {
        const first = items[0]
          ? { ...items[0], proportion: '1' }
          : { harvestId: '', proportion: '1' };
        entry.items = [first];
      }

      produce[produceTabIndex] = entry;
      return { ...prev, produce };
    });
  };

const currencySelectSx = {
  minWidth: 56,
  '& .MuiSelect-select': {
    py: 0.75,
    px: 1,
    fontSize: '0.85rem',
    opacity: 0.7,
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 18,
    opacity: 0.65,
  },
  '&:hover .MuiSelect-select, &:focus-within .MuiSelect-select': {
    opacity: 1,
  },
  '&:hover .MuiSvgIcon-root, &:focus-within .MuiSvgIcon-root': {
    opacity: 1,
  },
};


  // ---- SAVE handler with updated validation ----
  async function handleSave() {
    setFormSubmitted(true);

    // 1) Basic validation
    const {
      customerName,
      deliveryDate,
      produce: rawProduce,
      recurring,
      notes,
      deliveryPrice,
      deliveryPriceCurrency,
    } = newOrder;

    if (!customerName || !deliveryDate) {
      setError('Customer and delivery date are required.');
      return;
    }

    // every blend line needs portions + a selected harvest
    for (const entry of rawProduce) {
      if (!entry.portions) {
        setError('Please enter a portions value for each product.');
        return;
      }
      const rawPrice = String(entry.price ?? '').trim();
      if (rawPrice === '') {
        setError('Please enter a price per portion for each product. Use 0 for free items.');
        return;
      }
      for (const line of entry.items) {
        if (!line.harvestId) {
          setError('Please select a harvest for every blend line.');
          return;
        }
      }
    }

    if (!user?.uid) {
      setError('User not logged in.');
      return;
    }
    if (!activeFarmId) {
      setError('No active farm selected.');
      return;
    }
    const uid = user.uid;
    const isoDateString = deliveryDate.toISOString();

    // determine customerId
    const selectedCustomer = customers.find(
      (c) => c.businessName === customerName
    );
    const customerId = selectedCustomer?.id || '';

    // normalizeProduce handles both array & legacy "id/id/..." strings
    function normalizeProduce(prodField) {
      if (Array.isArray(prodField)) {
        return prodField.map((e) => ({
          items: e.items.map((it) => ({
            harvestId: it.harvestId,
            proportion: Number(it.proportion) || 0,
          })),
          portions: Number(e.portions) || 0,
          price: Number(e.price) || 0,     // preserve price
          priceCurrency: e.priceCurrency
        }));
      }
      if (typeof prodField === 'string') {
        const ids = prodField.split('/');
        const share = 1 / ids.length;
        return ids.map((hid) => ({
          items: [{ harvestId: hid, proportion: share }],
          portions: 1,
        }));
      }
      return [];
    }

    // 2) Fetch the old produce from Firestore if editing
    let oldDataProduce = [];
    if (editMode && orderToEdit?.id) {
      const snap = await getDoc(doc(db, 'farms', activeFarmId, 'orders', orderToEdit.id));
      if (snap.exists()) oldDataProduce = snap.data().produce || [];
    }

    // 2a) SANITIZE: remove any lines pointing at a deleted harvest
    const existingIds = new Set(harvests.map(h => String(h.id)));
    const sanitize = arr =>
      arr
        .map(entry => ({
          ...entry,
          items: entry.items.filter(it => existingIds.has(it.harvestId)),
        }))
        .filter(entry => entry.items.length);

    const sanitizedOldProd = sanitize(
      Array.isArray(oldDataProduce) ? oldDataProduce : []
    );
    const sanitizedRaw = sanitize(rawProduce);


    // 3) Build numeric arrays
    const oldProd = normalizeProduce(sanitizedOldProd);
    const newProd = normalizeProduce(sanitizedRaw);

    // 3a) Clean out any lines whose harvest was deleted
    const cleanedProd = newProd.map(entry => {
      const goodItems = entry.items.filter(
        it => harvests.some(h => String(h.id) === it.harvestId)
      );
      return { ...entry, items: goodItems };
    });

    // 4) Availability guard
    for (const entry of cleanedProd) {
      for (const line of entry.items) {
        const harvest = harvests.find(h => String(h.id) === line.harvestId);
        const available = Number(harvest?.portionsLeft) || 0;
        const required = entry.portions * line.proportion;
        if (available < required) {
          const seedName = seedLookup[harvest.seedId]?.name || 'Unknown';
          const dateStr = harvest.harvestDate
            ? format(parseISO(harvest.harvestDate), defaultDateFormat)
            : 'No Date';
          setError(
            `Not enough ${seedName} (${dateStr}): only ${available.toFixed(2)} portions available, but ${required.toFixed(2)} requested.`
          );
          return;
        }
      }
    }

    // 5) Adjust stock in one pass
    try {
      await adjustPortionsForEdit(activeFarmId, oldProd, cleanedProd);
    } catch (err) {
      console.error('Stock update failed', err);
      setError('Failed to update stock levels.');
      return;
    }

    // 6) Create or update the order
    let orderId, orderNumber;
    try {
      if (editMode && orderToEdit.id) {
        orderId = orderToEdit.id;
        orderNumber = orderToEdit.orderNumber;
        await updateDoc(
          doc(db, 'farms', activeFarmId, 'orders', orderId),
          {
            ...newOrder,
            customerId,
            deliveryDate: isoDateString,
            produce: cleanedProd,
          }
        );
        trackFeature('order_updated', {
          orderId,
          orderNumber,
          customerId,
        });
      } else {
        orderNumber = await getNextOrderNumber(activeFarmId);
        const ref = await addDoc(
          collection(db, 'farms', activeFarmId, 'orders'),
          {
            ...newOrder,
            customerId,
            deliveryDate: isoDateString,
            orderNumber,
            produce: cleanedProd,
          }
        );
        orderId = ref.id;
        trackFeature('order_created', {
          orderId,
          orderNumber,
          customerId,
        });
      }
    } catch (err) {
      console.error('Order write failed', err);
      setError('Error saving order.');
      return;
    }

    // 7) Recreate the delivery task
    if (orderId) {
      if (editMode) {
        const tq = query(
          collection(db, 'farms', activeFarmId, 'tasks'),
          where('orderId', '==', orderId),
          where('type', '==', 'delivery')
        );
        const snap = await getDocs(tq);
        await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
      }
      const task = generateTaskForOrderDelivery({
        orderId,
        orderNumber,
        deliveryDate: isoDateString,
      });
      if (task) {
        await addDoc(collection(db, 'farms', activeFarmId, 'tasks'), task);
      }
    }

    // 8) All done!
    setSuccess(editMode ? 'Order updated.' : 'Order created.');
    onAdd?.();
    onClose();
  }



  // ---- end save ----

  const handleCloseDialog = () => {
    setFormSubmitted(false);
    setTouched({});
    onClose();
  };

  const handleDialogKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCloseDialog();
      return;
    }

    if (e.key !== 'Enter') return;
    if (e.defaultPrevented || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;

    const target = e.target;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'textarea' || target?.isContentEditable) return;
    if (target?.closest?.('[role="listbox"], [role="menu"]')) return;

    e.preventDefault();
    handleSave();
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} onKeyDown={handleDialogKeyDown} fullWidth>
      <DialogTitle>
        {editMode ? 'Edit Order' : 'Add New Order'}
      </DialogTitle>
      <DialogContent>
        {/* -------- Customer & Date -------- */}
        <FormControl
          fullWidth
          margin="dense"
          required
          error={
            !newOrder.customerName &&
            (touched.customerName || formSubmitted)
          }
        >
          <InputLabel>Customer Name</InputLabel>
          <Select
            value={newOrder.customerName}
            onChange={(e) =>
              setNewOrder((p) => ({
                ...p,
                customerName: e.target.value,
              }))
            }
            onBlur={() => handleBlur('customerName')}
          >
            {customerOptions.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
          {!newOrder.customerName &&
            (touched.customerName || formSubmitted) && (
              <FormHelperText>
                Required
              </FormHelperText>
            )}
        </FormControl>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Delivery Date"
                value={newOrder.deliveryDate}
                onChange={(v) =>
                  setNewOrder((p) => ({
                    ...p,
                    deliveryDate: v,
                  }))
                }
                inputFormat={defaultDateFormat}
                disableMaskedInput
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="dense"
                    required
                    onBlur={() => handleBlur('deliveryDate')}
                    error={
                      !newOrder.deliveryDate &&
                      (touched.deliveryDate || formSubmitted)
                    }
                    helperText={
                      !newOrder.deliveryDate &&
                        (touched.deliveryDate || formSubmitted)
                        ? 'Required'
                        : ''
                    }
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <PriceField
                  label="Delivery Price"
                  fullWidth
                  value={newOrder.deliveryPrice}
                  onChange={(val) =>
                    setNewOrder((prev) => ({
                      ...prev,
                      deliveryPrice: val,
                    }))
                  }
                  helperText="Optional. Leave blank for pickup/free delivery."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {renderCurrencySelect(
                          newOrder.deliveryPriceCurrency,
                          (e) =>
                            setNewOrder((prev) => ({
                              ...prev,
                              deliveryPriceCurrency: e.target.value,
                            }))
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
            </Box>
          </Grid>
        </Grid>

        {/* Notes */}
        <TextField
          label="Notes"
          placeholder="Notes pertaining to this order, possibly bank details for payments or such. Use the template feature to save any standard notes."
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
          value={newOrder.notes}
          onChange={(e) =>
            setNewOrder((p) => ({
              ...p,
              notes: e.target.value,
            }))
          }
        />
        {/* Note-templates chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, mt: 1 }}>
          {noteTemplates.map((tpl) => (
            <Chip
              key={tpl.id}
              label={tpl.templateName}
              onClick={() => {
                trackFeature('order_note_template_loaded', {
                  templateId: tpl.id,
                  templateName: tpl.templateName,
                });
                setNewOrder(p => ({ ...p, notes: tpl.notes }));
              }}
              onDelete={async () => {
                if (!activeFarmId) {
                  setError?.('No active farm selected.');
                  return;
                }
                await deleteDoc(doc(db, 'farms', activeFarmId, 'orderNoteTemplates', tpl.id));
                trackFeature('order_note_template_deleted', {
                  templateId: tpl.id,
                });
                setSuccess?.('Template deleted.');
              }}
            />
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />
        <Box
          sx={{
            mb: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Produce
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Add items and optional blends
            </Typography>
          </Box>
          <FormControlLabel
            label={isBlendMode ? 'Blend On' : 'Blend Off'}
            labelPlacement="start"
            sx={{ mr: 0, ml: 0 }}
            control={
              <Switch
                checked={isBlendMode}
                onChange={handleBlendModeToggle}
                inputProps={{ 'aria-label': 'Blend mode' }}
              />
            }
          />
        </Box>

        {/* -------- Produce Tabs -------- */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Tabs
            value={produceTabIndex}
            onChange={handleProduceTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            textColor="secondary"
            indicatorColor="secondary"
            sx={{ mb: 2 }}
          >
            {newOrder.produce.map((_, idx) => (
              <Tab
                key={idx}
                label={`Produce ${idx + 1}`}
                iconPosition="end"
                icon={
                  // only allow removing additional products, not the first
                  newOrder.produce.length > 1 ? (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProduceTab(idx);
                      }}
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: 4,
                        p: 0,
                      }}
                    >
                      <CloseIcon fontSize="small" color="error" />
                    </IconButton>
                  ) : null
                }
              />
            ))}
            <Grid item xs={1}>
              <IconButton onClick={handleAddProduceTab} >
                <AddIcon color="secondary" />
              </IconButton>
            </Grid>
          </Tabs>

          {/* -------- Blend lines -------- */}
          {(isBlendMode ? activeProduce.items : [activeProduce.items[0]]).map(
            (item, idx) => (
              <Grid
                container
                spacing={2}
                alignItems="center"
                key={idx}
                sx={{ mb: 1 }}
              >
                <Grid item xs={isBlendMode ? 8 : 12}>
                  <FormControl
                    fullWidth
                    margin="dense"
                    required
                    error={formSubmitted && !item.harvestId}
                  >
                    <InputLabel>Harvest</InputLabel>
                    <Select
                      value={item.harvestId}
                      onChange={(e) =>
                        handleBlendItemChange(
                          idx,
                          'harvestId',
                          e.target.value
                        )
                      }
                      label="Harvest"
                    >
                      {mergedHarvestsOptions.official.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.label}
                        </MenuItem>
                      ))}
                      {mergedHarvestsOptions.extra.length > 0 && (
                        <ListSubheader>Other Harvests</ListSubheader>
                      )}
                      {mergedHarvestsOptions.extra.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formSubmitted && !item.harvestId ? (
                      <FormHelperText>Required</FormHelperText>
                    ) : null}
                  </FormControl>
                </Grid>
                {isBlendMode && (
                  <Grid item xs={3}>
                    <TextField
                      label="Proportion"
                      value={item.proportion}
                      onChange={(e) =>
                        handleBlendItemChange(
                          idx,
                          'proportion',
                          e.target.value
                        )
                      }
                      onBlur={() => rebalanceOthers(idx)}
                      placeholder="e.g. 0.50"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>
                )}
                {isBlendMode && (
                  <Grid item xs={1}>
                    {/* only show ?X? on non-first lines */}
                    {(
                      activeProduce.items.length > 1
                      ||
                      !harvests.find(h => String(h.id) === item.harvestId)
                    ) && (
                        <IconButton
                          size="small"
                          onClick={() => removeBlendItem(idx)}
                        >
                          <CloseIcon fontSize="small" color="error" />
                        </IconButton>
                      )}
                  </Grid>
                )}
              </Grid>
            )
          )}

          {isBlendMode && (
            <Button
              startIcon={<AddIcon />}
              onClick={addBlendItem}
              sx={{ alignSelf: 'flex-start', mb: 2 }}
            >
              Add blend line
            </Button>
          )}

          {/* -------- Portions & Price -------- */}
          <Box
            sx={{
              backgroundColor: (t) => t.palette.action.hover,
              borderRadius: 1.5,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Pricing
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Portions"
                  fullWidth
                  value={activeProduce.portions}
                  onChange={(e) =>
                    handleProduceFieldChange(
                      'portions',
                      e.target.value
                    )
                  }
                  margin="dense"
                />
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <PriceField
                    label="Price Per Portion"
                    fullWidth
                    required
                    value={activeProduce.price}
                    onChange={(val) => handleProduceFieldChange('price', val)}
                    error={formSubmitted && String(activeProduce.price ?? '').trim() === ''}
                    helperText={
                      formSubmitted && String(activeProduce.price ?? '').trim() === ''
                        ? 'Required'
                        : 'Enter 0 for free items.'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {renderCurrencySelect(activeProduce.priceCurrency, (e) =>
                            handleProduceFieldChange('priceCurrency', e.target.value)
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* === Actions Block === */}
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Left: Save Notes Template */}
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SaveIcon />}
            onClick={() => setNoteTplConfirmOpen(true)}
            disabled={!newOrder.notes.trim()}
          >
            Save Notes
          </Button>

          {/* Right: Cancel / Add */}
          <Box>
            <Button variant="text" onClick={handleCloseDialog} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button variant="contained" color="secondary" onClick={handleSave}>
              {editMode ? 'Update' : 'Add'}
            </Button>
          </Box>
        </Box>

        {/* ConfirmationDialog for naming the notes-template */}
        <ConfirmationDialog
          open={noteTplConfirmOpen}
          title="Save Notes as Template?"
          message="Enter a name for this note template"
          showInput
          onConfirm={async (tplName) => {
            if (!activeFarmId) {
              setError?.('No active farm selected.');
              return;
            }
            const payload = { templateName: tplName.trim(), notes: newOrder.notes };
            trackFeature('order_note_template_saved', {
              templateName: tplName.trim(),
            });
            await addDoc(collection(db, 'farms', activeFarmId, 'orderNoteTemplates'), payload);
            setNoteTplConfirmOpen(false);
            setNoteTplName('');
            setSuccess?.('Note template saved.');
          }}
          onClose={() => setNoteTplConfirmOpen(false)}
        >
          <TextField
            fullWidth
            label="Template Name"
            value={noteTplName}
            onChange={(e) => setNoteTplName(e.target.value)}
            autoFocus
            sx={{ mt: 2 }}
          />
        </ConfirmationDialog>
      </DialogContent>
    </Dialog>
  );
}

// (Optional) if you still need this helper elsewhere
function isSubscriptionActive(userData) {
  if (!userData || !userData.accessExpires) return true;
  const now = new Date();
  const expires = userData.accessExpires.toDate
    ? userData.accessExpires.toDate()
    : new Date(userData.accessExpires);
  return now <= expires;
}






